<template>
  <Modal class="nickname-modal" v-if="modals.nickname" @close="closeModal">
    <h3>{{ $t("settings.nickname") }}</h3>

    <div class="input-group">
      <label>{{ $t("settings.enterNickname") }}</label>
      <input
        type="text"
        v-model="nicknameInput"
        :placeholder="$t('settings.nickname')"
        maxlength="50"
        @keyup.enter="saveNickname"
        ref="nicknameInput"
      />
    </div>

    <div class="button-group">
      <div class="button save" @click="saveNickname">
        {{ $t("common.save") }}
      </div>
      <div class="button cancel" @click="closeModal">
        {{ $t("common.cancel") }}
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "./Modal";
import { mapState } from "vuex";

export default {
  components: {
    Modal,
  },
  computed: {
    ...mapState(["modals", "grimoire", "session"]),
  },
  data() {
    return {
      nicknameInput: "",
    };
  },
  watch: {
    "modals.nickname"(isOpen) {
      if (isOpen) {
        // Load current nickname when modal opens
        this.nicknameInput = this.grimoire.nickname || "";
        // Focus input after modal renders
        this.$nextTick(() => {
          if (this.$refs.nicknameInput) {
            this.$refs.nicknameInput.focus();
            this.$refs.nicknameInput.select();
          }
        });
      }
    },
  },
  methods: {
    saveNickname() {
      const nickname = this.nicknameInput.trim();
      const oldNickname = this.grimoire.nickname;
      this.$store.commit("setNickname", nickname);
      
      // If connected to a session and nickname changed, broadcast the update
      if (this.session.sessionId && nickname !== oldNickname) {
        this.$store.commit("session/updateNickname", nickname);
        
        // If this is the storyteller, update the storyteller fabled character locally
        if (!this.session.isSpectator) {
          const fabled = this.$store.state.players.fabled;
          const storytellerFabled = fabled.find((f) => f.isStoryteller);
          if (storytellerFabled) {
            const storytellerName = nickname || this.$t("session.storyteller") || "Storyteller";
            this.$store.commit("players/updateFabledName", {
              fabled: storytellerFabled,
              name: storytellerName,
            });
          }
        }
      }
      
      this.closeModal();
      
      // Check if there's a pending action to execute after nickname is set
      const pendingAction = this.session.pendingAction;
      if (pendingAction && nickname) {
        console.log("[NicknameModal] Executing pending action:", pendingAction);
        this.$store.commit("session/setPendingAction", null);
        
        // Execute the pending action after a short delay to ensure modal is closed
        this.$nextTick(() => {
          setTimeout(() => {
            if (pendingAction === "hostSession") {
              this.$store.commit("toggleModal", "sessionSetup");
            } else if (pendingAction === "joinSession") {
              this.$store.commit("toggleModal", "joinSession");
            }
          }, 100);
        });
      }
    },
    closeModal() {
      this.$store.commit("toggleModal", "nickname");
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../vars.scss";

.nickname-modal {
  ::v-deep .top-right-buttons {
    display: none !important;
  }

  h3 {
    display: none !important;
  }

  .input-group {
    margin-bottom: 15px;

    label {
      display: block;
      margin-bottom: 10px;
      font-size: 17px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.95);
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      letter-spacing: 0.3px;
    }

    input {
      width: 100%;
      padding: 14px 18px;
      font-size: 16px;
      border: 2px solid transparent;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.98);
      color: #2c3e50;
      box-sizing: border-box;
      transition: all 0.3s ease;
      font-weight: 500;

      &:focus {
        outline: none;
        background: white;
        border-color: #4a90e2;
        box-shadow: 0 0 0 4px rgba(74, 144, 226, 0.15),
                    0 4px 12px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
      }

      &::placeholder {
        color: #95a5a6;
        font-weight: 400;
      }
    }
  }

  .button-group {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    justify-content: flex-end;

    .button {
      padding: 6px 16px;
      border-radius: 20px;
      font-size: 13px;
      font-weight: 500;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      border: none;

      &.save {
        background: #007bff;
        color: white;

        &:hover {
          background: #0069d9;
          box-shadow: 0 3px 6px rgba(0, 123, 255, 0.25);
        }

        &:active {
          background: #0056b3;
          transform: scale(0.98);
        }
      }

      &.cancel {
        background: #dc3545;
        color: white;

        &:hover {
          background: #c82333;
          box-shadow: 0 3px 6px rgba(220, 53, 69, 0.25);
        }

        &:active {
          background: #bd2130;
          transform: scale(0.98);
        }
      }
    }
  }
}
</style>
